import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Header from '@/components/Header'
import { usePageTitle, useSeo } from '@/components/usePageTitle'
import { describeColor, isHex, randomHex, slugToName, titleSize } from '@/lib/color'
import { lookup } from '@/lib/api'

const YEAR = new Date().getFullYear()
const EXAMPLE_SLUGS = new Set(['example', 'random', 'sample', 'test'])

/** The tab icon becomes the color itself with the CM mark on it. */
function swatchIcon(hex: string, fg: 'black' | 'white'): string {
  const text = fg === 'black' ? '#111111' : '#ffffff'
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="${hex}"/><text x="32" y="44" font-family="Futura,'Century Gothic',Arial,sans-serif" font-size="30" font-weight="600" fill="${text}" text-anchor="middle">CM</text></svg>`
  return 'data:image/svg+xml,' + encodeURIComponent(svg)
}

interface Resolved {
  name: string
  hex: string
  slug: string
}

/**
 * The page every registered color gets at
 * <slug>.colormark.org. Full-bleed color, giant name with the CM mark, hex with
 * expandable color values, and the seal in the footer.
 */
export default function ColorPage({ slug: slugProp }: { slug?: string }) {
  const params = useParams<{ slug: string }>()
  const slug = (slugProp ?? params.slug ?? 'example').toLowerCase()
  const isExample = EXAMPLE_SLUGS.has(slug)
  const example = useMemo<Resolved | null>(
    () => (isExample ? { name: slug.charAt(0).toUpperCase() + slug.slice(1) + ' Color', hex: randomHex(), slug: 'example' } : null),
    [isExample, slug],
  )
  const [fetched, setFetched] = useState<{ slug: string; resolved: Resolved | null } | null>(null)

  useEffect(() => {
    if (isExample) return
    let alive = true
    lookup(slug).then((c) => {
      if (!alive) return
      setFetched({ slug, resolved: c ? { name: c.name, hex: c.hex, slug: c.slug } : null })
    })
    return () => {
      alive = false
    }
  }, [slug, isExample])

  const resolved = example ?? (fetched && fetched.slug === slug ? fetched.resolved : 'loading')
  if (resolved === 'loading') return <Loading hex={isHex(slug) ? '#' + slug.replace(/^#/, '') : '#ffffff'} />
  if (!resolved) return <NoColor slug={slug} />
  return <ColorView key={resolved.hex} {...resolved} />
}

function Loading({ hex }: { hex: string }) {
  return <div className="min-h-svh" style={{ backgroundColor: hex }} aria-busy="true" />
}

function ColorView({ name, hex, slug }: Resolved) {
  const v = describeColor(hex)
  const fg = v.contrast
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  useSeo({
    title: `${name} - ${v.hex}`,
    description: `${name} (${v.hex}) is registered with ColorMark. Color values, certificate, and merchandise.`,
    path: slug === 'example' ? '/color/example' : `/color/${slug}`,
    image: `/api/swatch?hex=${v.hex.slice(1)}`,
    imageAlt: `${name}, ${v.hex}`,
    themeColor: v.hex,
    icon: swatchIcon(v.hex, fg),
    noindex: slug === 'example',
  })

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(text)
      setTimeout(() => setCopied(null), 800)
    } catch {
      /* clipboard unavailable */
    }
  }

  const rows: [string, string][] = [
    ['HEX', v.hex],
    ['RGB', v.rgb.join(', ')],
    ['CMYK', v.cmyk.map((n) => n + '%').join(', ')],
    ['HSV', `${v.hsv[0]}°, ${v.hsv[1]}%, ${v.hsv[2]}%`],
    ['HSL', `${v.hsl[0]}°, ${v.hsl[1]}%, ${v.hsl[2]}%`],
  ]

  return (
    <div className="relative flex min-h-svh flex-col font-display" style={{ backgroundColor: v.hex, color: fg }}>
      <main className="flex-1 px-[36px] pt-[27px] pb-10">
        <h1
          className="break-words pb-5 font-light"
          style={{ fontSize: `calc(8vw + ${titleSize(name)}rem)`, hyphens: 'auto', lineHeight: 1.05 }}
        >
          {name}
          <sup>CM</sup>
        </h1>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="color-values"
          className="inline-flex items-center gap-2 text-2xl font-light"
        >
          {v.hex}
          <svg viewBox="0 0 16 16" className="h-[0.5em] w-[0.5em] fill-current" aria-hidden="true">
            <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
            {open ? (
              <path fillRule="evenodd" d="M3.5 8a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1H4a.5.5 0 0 1-.5-.5z" />
            ) : (
              <>
                <path fillRule="evenodd" d="M8 3.5a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5H4a.5.5 0 0 1 0-1h3.5V4a.5.5 0 0 1 .5-.5z" />
                <path fillRule="evenodd" d="M7.5 8a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1H8.5V12a.5.5 0 0 1-1 0V8z" />
              </>
            )}
          </svg>
        </button>
        {open && (
          <dl id="color-values" className="mt-4 text-lg font-light">
            {rows.map(([label, value]) => (
              <div key={label} className="pt-3">
                <dt className="text-base leading-none">{label}</dt>
                <dd className="relative mt-1 inline-block">
                  <button type="button" onClick={() => copy(value)} className="cursor-pointer hover:underline" title="Copy">
                    {value}
                  </button>
                  {copied === value && (
                    <span className="absolute left-full top-1/2 ml-3 -translate-y-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white">
                      Copied: {value}
                    </span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </main>

      <footer className="flex items-end justify-between px-[36px] pb-[27px] font-body text-sm font-extralight">
        <Link to="/" className="no-underline" style={{ color: fg }}>
          Copyright © {YEAR} ColorMark™ All Rights Reserved
        </Link>
        <div className="flex items-end gap-8">
          <Link to={`/color/${slug}/merch`} className="no-underline hover:underline" style={{ color: fg }}>
            Merchandise
          </Link>
          <Link to={`/color/${slug}/certificate`} className="flex flex-col items-center no-underline" style={{ color: fg }}>
            <img src={`/images/cm-seal-128-${fg}.svg`} alt="" width={48} height={48} />
            <span className="mt-1 leading-tight">Verify Registration</span>
          </Link>
        </div>
      </footer>
      {slug === 'example' && (
        <p className="absolute bottom-20 right-[36px] font-body text-xs opacity-70">
          Example page.{' '}
          <Link to="/register" style={{ color: fg }}>
            Register
          </Link>{' '}
          to get your own.
        </p>
      )}
    </div>
  )
}

/** The view for a slug that is not a registered color. */
export function NoColor({ slug }: { slug: string }) {
  const hex = isHex(slug)
  const label = hex ? '#' + slug.replace(/^#/, '').toLowerCase() : slugToName(slug)
  const cat = hex ? 'Code' : 'Name'
  const to = hex ? `/register?hex=${slug.replace(/^#/, '')}` : '/register'
  usePageTitle('Color Not Found')

  return (
    <div className="relative flex min-h-svh flex-col bg-black text-white">
      <Header tone="light" />
      <main className="flex flex-1 flex-col items-center justify-center px-6 pt-36 text-center">
        <h1 className="font-light">
          {label} <br /> not found :(
        </h1>
        <h3 className="mt-12 font-light italic">This Color {cat} may be available to register.</h3>
        <h1 className="mt-6">
          <Link to={to} className="font-normal">
            Register Now!
          </Link>
        </h1>
      </main>
      <footer className="px-[36px] pb-[27px] font-body text-sm font-extralight">
        Copyright © {YEAR} ColorMark™
        <br />
        All Rights Reserved
      </footer>
    </div>
  )
}
